import React, { useEffect, useReducer, useCallback } from "react";
import { Modal } from "./ds/Modal";
import { Button } from "./ds/Button";
import { registerModalConfirm } from "../lib/modalFacade";

const initial = { open: false, payload: null };

function reducer(state, action) {
  switch (action.type) {
    case "open":
      return { open: true, payload: action.payload };
    case "close":
      return initial;
    case "loading":
      return state.payload
        ? {
            open: true,
            payload: { ...state.payload, confirmLoading: action.value },
          }
        : state;
    default:
      return state;
  }
}

export function ConfirmModalProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initial);

  const runConfirm = useCallback((opts) => {
    return new Promise((resolve) => {
      dispatch({
        type: "open",
        payload: {
          title: opts.title,
          content:
            typeof opts.content !== "undefined"
              ? opts.content
              : opts.icon
                ? opts.icon
                : null,
          okText: opts.okText ?? opts.okButtonProps?.children ?? "OK",
          cancelText:
            opts.cancelText ?? opts.cancelButtonProps?.children ?? "Cancel",
          okType:
            opts.okType === "danger" || opts.okButtonProps?.danger
              ? "danger"
              : "primary",
          onOk: opts.onOk,
          onCancel: opts.onCancel,
          resolve,
          confirmLoading: false,
        },
      });
    });
  }, []);

  useEffect(() => {
    registerModalConfirm(runConfirm);
    return () => registerModalConfirm(null);
  }, [runConfirm]);

  const finalize = useCallback(
    async (accepted) => {
      const p = state.payload;
      if (!p) return;
      try {
        if (!accepted && p.onCancel) await Promise.resolve(p.onCancel());
      } catch {
        /* ignore */
      } finally {
        p.resolve(accepted);
        dispatch({ type: "close" });
      }
    },
    [state.payload]
  );

  const handleOk = async () => {
    const p = state.payload;
    if (!p) return;
    if (!p.onOk) {
      await finalize(true);
      return;
    }
    dispatch({ type: "loading", value: true });
    try {
      await Promise.resolve(p.onOk());
      await finalize(true);
    } catch {
      dispatch({ type: "loading", value: false });
    }
  };

  const payload = state.payload;

  return (
    <>
      {children}
      {state.open && payload ? (
        <Modal
          open
          title={payload.title}
          onCancel={() => finalize(false)}
          footer={
            <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
              <Button
                variant="ghost"
                type="button"
                onClick={() => finalize(false)}
              >
                {payload.cancelText}
              </Button>
              <Button
                variant={payload.okType === "danger" ? "danger" : "primary"}
                type="button"
                loading={payload.confirmLoading}
                onClick={handleOk}
              >
                {payload.okText}
              </Button>
            </div>
          }
        >
          <div className="text-sm text-slate-700">{payload.content}</div>
        </Modal>
      ) : null}
    </>
  );
}
